// ============================================================================
// COMPONENTE: Dialog para visualizar detalhes do caixa/agendamento
// ============================================================================
// Funcionalidades:
// - Exibir detalhes completos do caixa ou agendamento
// - Mostrar informações do paciente, profissional, valores e pagamentos
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import {
  FaCashRegister,
  FaCalendarAlt,
  FaClock,
  FaBuilding,
  FaUserMd,
  FaUser,
  FaIdCard,
  FaPhone,
  FaEnvelope,
  FaDollarSign,
  FaCreditCard,
  FaCheckCircle,
  FaTooth,
} from "react-icons/fa";

const DialogDetalhes = ({
  visible,
  onHide,
  dados,
  formatarData,
  formatarHorario,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detecção de dispositivo móvel (hooks primeiro)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!dados) return null;

  const isCaixa = dados.hasOwnProperty("valor_total");
  const isAgenda = dados.hasOwnProperty("servico_nome");

  // Estilos compartilhados
  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  return (
    <Dialog
      visible={visible}
      style={{
        width: "90vw",
        maxWidth: "768px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={onHide}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              {isCaixa ? (
                <FaCashRegister
                  style={{ fontSize: "2.4rem", color: "#B83732" }}
                />
              ) : (
                <FaCalendarAlt
                  style={{ fontSize: "2.4rem", color: "#B83732" }}
                />
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                DETALHES DO
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                {isCaixa ? "CAIXA" : "AGENDAMENTO"}
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div
        style={{
          backgroundColor: "#FAFAFA",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {/* ========== SEÇÃO 1: INFORMAÇÕES DO PACIENTE ========== */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          {/* Card Informações do Paciente */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "15px",
            }}
          >
            <div style={labelStyle}>
              <FaUser style={iconStyle} />
              <span>INFORMAÇÕES DO PACIENTE</span>
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#4a5568",
                marginTop: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "4px",
                }}
              >
                <span style={{ color: "#64748b" }}>Nome:</span>
                <span style={{ fontWeight: "500", textAlign: "right" }}>
                  {dados.paciente_nome}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "4px",
                }}
              >
                <span style={{ color: "#64748b" }}>CPF:</span>
                <span>{dados.paciente_cpf}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "4px",
                }}
              >
                <span style={{ color: "#64748b" }}>Telefone:</span>
                <span>{dados.paciente_telefone || "---"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b" }}>Email:</span>
                <span>{dados.paciente_email || "---"}</span>
              </div>
            </div>
          </div>

          {/* Card Profissional / Empresa */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "15px",
            }}
          >
            <div style={labelStyle}>
              <FaBuilding style={iconStyle} />
              <span>PROFISSIONAL / EMPRESA</span>
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#4a5568",
                marginTop: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "4px",
                }}
              >
                <span style={{ color: "#64748b" }}>Profissional:</span>
                <span style={{ fontWeight: "500" }}>
                  {dados.profissional_nome}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "4px",
                }}
              >
                <span style={{ color: "#64748b" }}>Especialidade:</span>
                <span>{dados.profissional_especialidade}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b" }}>Empresa:</span>
                <span>{dados.empresa_nome}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ========== SEÇÃO 2: DATA/HORÁRIO ========== */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "15px",
            marginBottom: "20px",
          }}
        >
          {/* Card Data e Horário */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "15px",
            }}
          >
            <div style={labelStyle}>
              <FaCalendarAlt style={iconStyle} />
              <span>DATA E HORÁRIO</span>
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#4a5568",
                marginTop: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "4px",
                }}
              >
                <span style={{ color: "#64748b" }}>Data:</span>
                <span style={{ fontWeight: "500" }}>
                  {formatarData(dados.data_atendimento)}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b" }}>Horário:</span>
                <span>{formatarHorario(dados.horario)}</span>
              </div>
            </div>
          </div>

          {/* Card Serviço / Valores */}
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "15px",
            }}
          >
            <div style={labelStyle}>
              {isCaixa ? (
                <FaDollarSign style={iconStyle} />
              ) : (
                <FaTooth style={iconStyle} />
              )}
              <span>{isCaixa ? "VALORES DO CAIXA" : "SERVIÇO"}</span>
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#4a5568",
                marginTop: "8px",
              }}
            >
              {isAgenda ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Serviço:</span>
                    <span style={{ fontWeight: "500" }}>
                      {dados.servico_nome}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "#64748b" }}>Valor:</span>
                    <span>
                      R$ {parseFloat(dados.servico_valor || 0).toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Status:</span>
                    <span
                      style={{
                        fontWeight: "600",
                        color:
                          dados.status === "fechado" ? "#27ae60" : "#e67e22",
                      }}
                    >
                      {dados.status?.toUpperCase()}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingBottom: "4px",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Valor Total:</span>
                    <span style={{ fontWeight: "500" }}>
                      R$ {parseFloat(dados.valor_total || 0).toFixed(2)}
                    </span>
                  </div>
                  {dados.data_fechamento && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "#64748b" }}>Fechamento:</span>
                      <span>{formatarData(dados.data_fechamento)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Card Formas de Pagamento */}
        {!isAgenda && dados.pagamentos.length > 0 && (
            <div
              style={{
                backgroundColor: "white",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "15px",
              }}
            >
              <div style={labelStyle}>
                <FaCreditCard style={iconStyle} />
                <span>FORMAS DE PAGAMENTO</span>
              </div>
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#4a5568",
                  marginTop: "8px",
                }}
              >
                {isCaixa && dados.pagamentos && dados.pagamentos.length > 0 && (
                    <div
                      style={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                      }}
                    >
                      {dados.pagamentos.map((pag, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "6px 0",
                            borderBottom:
                              idx !== dados.pagamentos.length - 1
                                ? "1px solid #e2e8f0"
                                : "none",
                          }}
                        >
                          <span
                            style={{ color: "#4a5568", fontSize: "0.85rem" }}
                          >
                            {pag.forma_pagamento_descricao || pag.descricao}
                          </span>
                          <span style={{ fontWeight: "500", color: "#2d3748" }}>
                            R$ {parseFloat(pag.valor || 0).toFixed(2)}
                          </span>
                        </div>
                      ))}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "8px",
                          paddingTop: "8px",
                          borderTop: "1px solid #e2e8f0",
                          fontWeight: "bold",
                        }}
                      >
                        <span style={{ color: "#2d3748" }}>TOTAL PAGO</span>
                        <span style={{ color: "#B83732" }}>
                          R${" "}
                          {dados.pagamentos
                            .reduce(
                              (acc, p) => acc + parseFloat(p.valor || 0),
                              0,
                            )
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                )}
              </div>
            </div>
        )}
      </div>

      {/* Botão Fechar */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "24px",
        }}
      >
        <Button
          label="FECHAR"
          icon="pi pi-times"
          onClick={onHide}
          className="p-button-outlined"
          style={{
            padding: "10px 28px",
            minWidth: "120px",
            borderColor: "#e2e8f0",
            color: "#718096",
            fontWeight: "600",
            borderRadius: "8px",
          }}
        />
      </div>
    </Dialog>
  );
};

export default DialogDetalhes;
